import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Bell, CheckCheck, Inbox } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Notification {
  id: string;
  kind: 'info' | 'success' | 'warning' | 'error';
  category: string;
  title: string;
  body: string | null;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();

  const count = useQuery({
    queryKey: ['notifications', 'count'],
    queryFn: async () => (await api.get('/api/notifications/count')).data,
    refetchInterval: 60000, // SSE updates it faster; this is a backstop
  });

  const list = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => (await api.get('/api/notifications?limit=20')).data,
    enabled: open,
  });

  const markAll = useMutation({
    mutationFn: async () => api.post('/api/notifications/read', { all: true }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'count'] });
    },
  });

  const markOne = useMutation({
    mutationFn: async (id: string) => api.post('/api/notifications/read', { ids: [id] }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] });
      qc.invalidateQueries({ queryKey: ['notifications', 'count'] });
    },
  });

  const unread = count.data?.unread ?? 0;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-2 w-[360px] animate-slide-up rounded-md border bg-popover shadow-popover">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <div className="font-medium">Notifications</div>
              {unread > 0 && (
                <button
                  onClick={() => markAll.mutate()}
                  className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
                >
                  <CheckCheck className="h-3 w-3" /> Mark all read
                </button>
              )}
            </div>
            <div className="max-h-[60vh] overflow-y-auto">
              {list.isLoading ? (
                <div className="p-6 text-center text-xs text-muted-foreground">Loading...</div>
              ) : !list.data?.items?.length ? (
                <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-xs text-muted-foreground">
                  <Inbox className="h-8 w-8 opacity-50" />
                  <p>You're all caught up.</p>
                </div>
              ) : (
                <div className="divide-y">
                  {(list.data.items as Notification[]).map((n) => {
                    const content = (
                      <div
                        className={cn(
                          'flex items-start gap-3 p-3 text-left transition-colors hover:bg-accent/40',
                          !n.readAt && 'bg-primary/5',
                        )}
                      >
                        <div
                          className={cn(
                            'mt-1 h-2 w-2 shrink-0 rounded-full',
                            n.kind === 'error'
                              ? 'bg-destructive'
                              : n.kind === 'warning'
                                ? 'bg-amber-500'
                                : n.kind === 'success'
                                  ? 'bg-emerald-500'
                                  : 'bg-primary',
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium">{n.title}</div>
                          {n.body && <div className="text-xs text-muted-foreground">{n.body}</div>}
                          <div className="mt-0.5 text-[10px] text-muted-foreground">
                            {formatRelative(n.createdAt)} · {n.category}
                          </div>
                        </div>
                      </div>
                    );
                    return n.link ? (
                      <Link
                        key={n.id}
                        to={n.link}
                        onClick={() => {
                          markOne.mutate(n.id);
                          setOpen(false);
                        }}
                        className="block"
                      >
                        {content}
                      </Link>
                    ) : (
                      <button
                        key={n.id}
                        onClick={() => markOne.mutate(n.id)}
                        className="block w-full"
                      >
                        {content}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function formatRelative(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
